import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from '../../../../core/shared/service/crud/mvs-crud.service';
import {LogicMappingTestRequestDto} from '../../model/dto/api/logic-mapping-test-request.dto';
import {LogicMappingTestResponseDto} from '../../model/dto/api/logic-mapping-test-response.dto';
import {LogicDependencyResultDto} from '../../model/dto/api/logic-dependency-result.dto';

/**
 * CRUD service for `lg.LogicMappingTable` plus the two endpoints that are not plain CRUD: the lookup
 * quick test and the dependency analysis.
 *
 * Exists because of those two methods — the generic `MvsCrudService` covers everything else.
 */
@Injectable({
    providedIn: 'root'
})
export class LogicMappingTableService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/lg/logicMappingTables');
    }

    /**
     * Resolves one source value against a mapping table and reports the result, the resolution path and
     * the server-measured duration.
     *
     * The lookup runs the production resolution path, so the numbers describe real behaviour. Three
     * things about the response are easy to get wrong and must not be smoothed over by the caller:
     * a business failure (nothing found against a hard `ERROR` fallback) arrives as HTTP 200 with
     * `success = false`; `targetValueIsAbsent` separates 'no value' from 'empty text'; and
     * `iterationsCompleted` can be lower than what was requested when `budgetExceeded` is set.
     *
     * @param mappingTableId id of the `lg.LogicMappingTable` to resolve against
     * @param request source value, optional partner alias, optional caller default, iteration count
     */
    testLookup(mappingTableId: number, request: LogicMappingTestRequestDto): Observable<LogicMappingTestResponseDto> {
        const url: string = `${this.apiUrl}/${mappingTableId}/testLookup`;

        return this.http.post<LogicMappingTestResponseDto>(url, request);
    }

    /**
     * Loads everything that references a mapping table THROUGH A TYPED FOREIGN KEY — `MAPPING_LOOKUP`
     * flow steps and logics of language `mapping`.
     *
     * The endpoint lives under `/mvsa/lg/dependencies`, not under this service's own CRUD path.
     *
     * Coverage limit: script calls (`$ctx.lookupMapping`) and direct Java calls are invisible to this
     * query, so an empty result means 'no typed reference', not 'unused'. Surfaces that show the
     * result must show `LG_MAPPING_TABLE_DEPENDENCY_COVERAGE_NOTE` with it.
     *
     * @param mappingTableId id of the `lg.LogicMappingTable` to analyse
     */
    getDependents(mappingTableId: number): Observable<LogicDependencyResultDto> {
        const url: string = `${MvsCrudService.baseUrl}/lg/dependencies/mapping-table/${mappingTableId}`;

        return this.http.get<LogicDependencyResultDto>(url);
    }

}
